import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import { DATE_FORMATS } from '@core/constants/date.constants';
import { isDarkMode } from '@core/services/theme.store';
import { TranslationService } from '@core/translation.service';
import { NotificationItem, NotificationRedirect } from '@features/notifications/models/notification-item';
import { TranslateModule } from '@ngx-translate/core';
import { PnycBadgeDirective } from '@ui-kit/directives/pnyc-badge/pnyc-badge.directive';
import { PnycAvatarComponent } from '@ui-kit/pnyc-avatar/pnyc-avatar.component';
import { PnycButtonComponent } from '@ui-kit/pnyc-button/pnyc-button.component';
import { PnycIconComponent } from '@ui-kit/pnyc-icon/pnyc-icon.component';
import { locale as english } from './i18n/en';

@Component({
    selector: 'notification-item',
    imports: [
        NgTemplateOutlet,
        DatePipe,
        TranslateModule,
        PnycAvatarComponent,
        PnycIconComponent,
        PnycButtonComponent,
        PnycBadgeDirective,
    ],
    templateUrl: './notification-item.component.html',
    styleUrl: './notification-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationItemComponent {
    public notification = input<NotificationItem>();
    public toggleCollapsed = output<void>();
    public markAsRead = output<{ id: number, isGroup: boolean, isRedirect?: boolean }>();
    public redirect = output<NotificationRedirect>();
    public readonly isDarkMode = isDarkMode;

    public isGroupViewed = false;

    public readonly dateFormat = DATE_FORMATS.SHORT_DATE_SLASH;

    private readonly notificationsPerExpansion = 5;

    private readonly translationLoaderService = inject(TranslationService);

    constructor() {
        this.translationLoaderService.loadTranslations(english);

        effect(() => {
            const n = this.notification();

            this.isGroupViewed = n?.childItems?.every(item => item.isViewed) ?? false;
        });
    }

    public backgroundImage = computed(() =>
        this.isDarkMode()
            ? 'url(./assets/images/decoration/notifications/decor-dark.svg)'
            : 'url(./assets/images/decoration/notifications/decor-light.svg)'
    );

    public get childItems(): NotificationItem[] | null {
        const n = this.notification();

        if (n == null) {
            return null;
        }

        return n.childItems?.slice(0, (n.expandedCount ?? 0) * this.notificationsPerExpansion) ?? null;
    }

    public get hasMoreItems(): boolean {
        const n = this.notification();

        if (n == null) {
            return false;
        }

        const totalItems = n.totalCount ?? n.childItems?.length ?? 0;
        const displayedItems = (n.expandedCount ?? 0) * this.notificationsPerExpansion;

        return totalItems > displayedItems;
    }

    public onMarkAsRead(event: MouseEvent, id: number, isGroup: boolean): void {
        this.markAsRead.emit({ id, isGroup });
        event.stopPropagation();
    }

    public onRedirect(item: NotificationItem): void {
        const path = item.redirectUrl ?? '';

        this.redirect.emit(item.selectsCustomerOnRedirect
            ? { path, customerId: item.customerId, collaborationId: item.collaborationId }
            : { path });
        this.markAsRead.emit({ id: item.id, isGroup: false, isRedirect: !!path });
    }

    public onCustomerNameClick(event: MouseEvent, customerProfileUrl: string): void {
        event.stopPropagation();

        if (customerProfileUrl) {
            this.redirect.emit({ path: customerProfileUrl });
        }
    }

    public getTitleParts(title: string, customerName: string): string[] {
        const parts: string[] = [];
        const index = title.indexOf(customerName);

        if (index === -1) {
            return [title];
        }

        if (index > 0) {
            parts.push(title.substring(0, index));
        }

        parts.push(customerName);

        if (index + customerName.length < title.length) {
            parts.push(title.substring(index + customerName.length));
        }

        return parts;
    }
}
